import "nprogress/nprogress.css";
import "antd/dist/antd.less";
import "styles/root.scss";
import "styles/base.scss";
import "styles/globals.scss";
import "styles/ant.scss";

import React from "react";
import { useRouter } from "next/router";
import Script from "next/script";
import Head from "next/head";
import { ConfigProvider, message } from "antd";
import NProgress from "nprogress";
import Storage from "utils/localStorage";
import AppCookie from "utils/cookie";

import "utils/expand";
import store from "store";
const Package = require("../../package.json");

import useWindowResize from "hooks/useWindowResize";
import { Context, XtLocale, Cookie, Request, Platform } from "@xt/xtbase";
import { isRtlLocale } from "@/utils/rtl";
const { XtContext, XtProvider } = Context;
import PWAInstallPrompt from "components/App/PWAInstallPrompt";
import PWAUpdatePrompt from "components/App/PWAUpdatePrompt";
import Seo from "components/Seo";
// import CustomerService from "components/App/CustomerService";
import { useServiceWorkerRegistration } from "@/hooks/useServiceWorkerRegistration";
import { getZendeskLocale } from "@/utils/method";
import { trackFbPixelPageView, getFbPixelId } from "@/utils/facebookPixel";
import { getCountryCodeByIP, getLocaleByCountryCode } from "@/utils/getDefaultLocaleByIP";
import { webIndexSpaPathNoIntlLocale, pathForVueSpaLocaleSkip } from "@/utils/webIndexSpaLocale";
import proxyApi from "../../proxyApi.json";

function shouldSkipLocaleUrlForVueSpa(router) {
  return webIndexSpaPathNoIntlLocale(pathForVueSpaLocaleSkip(router.asPath));
}

/** 仅非 Vue 托管路径改 URL；pathname 为 /_error、/404 等与地址栏不一致时用 asPath 做 replace */
function replaceLocaleInUrl(router, nextLocale) {
  if (!nextLocale || nextLocale === router.locale) return;
  if (shouldSkipLocaleUrlForVueSpa(router)) return;
  const { pathname, asPath } = router;
  const useAsPathOnly = pathname === "/_error" || pathname === "/404" || pathname === "/500";
  if (useAsPathOnly) {
    router.replace(asPath, asPath, { locale: nextLocale });
  } else {
    router.replace(pathname, asPath, { locale: nextLocale });
  }
}

/** 站点图标 / manifest / favicon 缓存版本：换图后递增，需与 public/manifest.json 中 ?v= 保持一致 */
const APP_ICON_VERSION = "2";
const faviconIco = `/favicon.ico?v=${APP_ICON_VERSION}`;
const MANUAL_LANG_COOKIE = "langManual";

const ContextToStore = ({ children }) => {
  const [appState, appDispatch] = React.useContext(XtContext);
  // const theme = children.props.theme || Cookie.get("theme") || "dark";
  // const currency = Cookie.get("currency");
  
  // 注册Service Worker
  useServiceWorkerRegistration();
  
  React.useEffect(() => {
    const payload = {};
    // 仅使用深色主题，默认黑色背景，避免页面切换时白屏闪烁
    payload.theme = "dark";
    const currency = Cookie.get("currency");
    currency && (payload.currency = currency);
    const colorReverse = Cookie.get("colorReverse") || Storage.get("colorReverse");
    payload.colorReverse = +colorReverse || 0;
    appDispatch({ payload });

    /*
    appDispatch({
      payload: {
        theme,
        currency
      }
    });
    //初始化折算币种
    if (currency) {
      store.balances.updateConvertCurrency(currency);
    }
    //初始化主题
    if (/^(dark|light)$/.test(theme)) {
      store.app.updateState({ theme });
    }
    //初始化token
    const token = Cookie.get("token");
    if (token) {
      store.user.updateState({ token });
    }
     */
  }, []);

  // AWS CloudWatch RUM（pro / qa 环境上报）
  React.useEffect(() => {
    if (typeof window === "undefined" || !["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV)) return;
    import("@/snippet").then((m) => m.initAwsRum());
  }, []);

  /*
  React.useEffect(() => {
    //同步isLogin
    store.user.updateState({ isLogin: appState.loginStatus == 1 });
  }, [appState.loginStatus]);
  React.useEffect(() => {
    //同步userInfo
    console.log(appState.userInfo, "appState.userInfo");
    store.user.updateState({ userInfo: appState.userInfo });
  }, [appState.userInfo]);
   */

  React.useEffect(() => {
    const isLogin = appState.loginStatus === 1;
    const token = isLogin ? Cookie.get("token") : "";

    store.app.updateState({
      theme: appState.theme,
      colorStyle: appState.colorReverse,
    });
    store.balances.updateState({
      convertCurrency: appState.currency,
    });
    store.user.updateState({
      isLogin,
      token,
      userInfo: appState.userInfo,
    });
    store.currency.updateAssist(appState.currency);
  }, [appState]);

  return children;
};

const localeToLangKey = (value) => {
  if (!value || value === "default") return undefined;
  if (value === "zh-CN") return "cn";
  if (value === "zh-HK") return "hk";
  return value;
};

const langKeyToLocale = (value, localeList = []) => {
  if (!value) return undefined;
  const nextLocale = value === "cn" ? "zh-CN" : value === "hk" ? "zh-HK" : value;
  if (localeList?.length && !localeList.includes(nextLocale)) return undefined;
  return nextLocale;
};

function MyApp({ Component, pageProps }) {
  reportWebVitals.nextRouterInfo = useRouter(); // 用于性能埋点绑定路由
  const router = useRouter();
  const { locale, locales, pathname } = router;
  const isRtl = isRtlLocale(locale);
  XtLocale.init(locale, locales, { [locale === "default" ? "en" : locale]: pageProps.messages });
  const localeObj = XtLocale.getLocaleObj(locale);
  const payload = { lang: localeObj.key };
  const seoLocale = pageProps.locale || (locale === "default" ? "en" : locale);
  const seoResolvedUrl =
    pageProps.resolvedUrl ||
    (() => {
      let path = (router.asPath || router.pathname || "").split("?")[0].split("#")[0];
      const prefix = `/${seoLocale}`;
      if (path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
      else if (path === prefix) path = "";
      if (path && !path.startsWith("/")) path = `/${path}`;
      return path || "/accounts/register";
    })();

  React.useEffect(() => {
    const dir = isRtl ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("data-dir", dir);
  }, [isRtl]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !router.isReady) return;
    // App WebView 内语言由 App 控制（URL locale），跳过 IP 检测
    if (Platform.isInApp()) return;
    const cookieLang = Cookie.get("lang");
    const hasManualLang = AppCookie.get(MANUAL_LANG_COOKIE) === "1";
    const preferredLocale = hasManualLang ? langKeyToLocale(cookieLang, locales) : undefined;
    if (preferredLocale) {
      if (preferredLocale !== locale) {
        replaceLocaleInUrl(router, preferredLocale);
      }
      return;
    }
    let cancelled = false;
    getCountryCodeByIP().then((countryCode) => {
      if (cancelled || !countryCode) return;
      const targetLocale = getLocaleByCountryCode(countryCode);
      Cookie.set("lang", targetLocale);
      if (targetLocale !== router.locale) {
        replaceLocaleInUrl(router, targetLocale);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locale, locales, router.asPath, router.isReady, pathname]);

  //初始化antd
  React.useEffect(() => {
    message.config({
      top: 60,
      maxCount: 3,
    });
    ConfigProvider.config({
      theme: {
        primaryColor: "#B9F78A",
      },
    });
  }, []);

  //初始化NProgress
  React.useEffect(() => {
    const handleRouteStart = () => {
      // console.log("NProgress.start()========");
      NProgress.start();
    };
    const handleRouteDone = () => {
      // console.log("NProgress.done()========");
      NProgress.done();
    };

    router.events.on("routeChangeStart", handleRouteStart);
    router.events.on("routeChangeComplete", handleRouteDone);
    router.events.on("routeChangeError", handleRouteDone);

    return () => {
      router.events.off("routeChangeStart", handleRouteStart);
      router.events.off("routeChangeComplete", handleRouteDone);
      router.events.off("routeChangeError", handleRouteDone);
    };
  }, [router.events]);

  const domain = !process.env.NEXT_PUBLIC_LOCAL ? process.env.NEXT_PUBLIC_cdn + "/public" : "";
  if (process.env.NEXT_PUBLIC_LOCAL) {
    Request.setHeader({ "X-Forwarded-Host": process.env.NEXT_PUBLIC_host });
  }

  // 配置 axios 请求拦截器，在客户端将相对路径转换为完整 URL
  if (typeof window !== "undefined") {
    const axiosInstance = Request.XtAxios.instance;
    
    // 需要代理的 API 路径前缀（从 proxyApi.json 读取）
    const proxyApiPaths = proxyApi || [];
    
    const requestInterceptor = (config) => {
      // 如果是相对路径且以需要代理的路径开头，转换为完整 URL
      if (config.url && typeof config.url === "string" && config.url.startsWith("/")) {
        const pathMatch = proxyApiPaths.find((path) => config.url.startsWith(`/${path}/`));
        if (pathMatch) {
          // 在客户端，始终使用配置的 NEXT_PUBLIC_host 和 NEXT_PUBLIC_protocol
          const host = process.env.NEXT_PUBLIC_host;
          const protocol = process.env.NEXT_PUBLIC_protocol;
          if (host && protocol) {
            config.url = `${protocol}//${host}${config.url}`;
          }
        }
      }
      return config;
    };
    
    // 为 XtAxios 添加请求拦截器（XtAxios4 使用同一个 instance）
    axiosInstance.interceptors.request.use(requestInterceptor);
  }

  useWindowResize(() => {
    let breakpoint;
    const { innerWidth } = window;
    if (innerWidth >= 1698) {
      breakpoint = "xl";
    } else if (innerWidth > 1200) {
      breakpoint = "lg";
    } else if (innerWidth > 768) {
      breakpoint = "md";
    } else {
      //<= 768
      breakpoint = "sm";
    }
    store.app.updateState({ breakpoint });
    document.body.setAttribute("data-breakpoint", breakpoint);
  });
  React.useEffect(() => {
    store.app.updateState({ clientSideReady: true });
    console.log("%c【App client side ready】", "color:#52c41a", store);
    // 客服已替换为 LiveHelp100，以下 Zendesk 加载注释保留，后续可能恢复
    // if (typeof document !== "undefined" && !document.getElementById("ze-snippet")) {
    //   document.body.classList.add("zendesk-hide");
    //   const script = document.createElement("script");
    //   script.setAttribute("src", "https://static.zdassets.com/ekr/snippet.js?key=8d996cc2-d7cd-4cab-a32d-d714c739f6ce");
    //   script.setAttribute("async", "true");
    //   script.setAttribute("id", "ze-snippet");
    //   script.setAttribute("crossorigin", "anonymous");
    //   script.onload = function () {
    //     const zE = typeof window !== "undefined" && (window.zE || window.$zE);
    //     if (typeof zE !== "function") return;
    //     const zenLocale = getZendeskLocale(XtLocale.getZenLan());
    //     try {
    //       zE("webWidget", "setLocale", zenLocale);
    //       zE("messenger:set", "locale", zenLocale);
    //       zE("webWidget", "hide");
    //       zE("messenger", "hide");
    //       zE("webWidget:on", "close", function () { try { zE("webWidget", "hide"); } catch (e) {} });
    //       var doMessengerHide = function () { try { zE("messenger", "hide"); } catch (e) {} };
    //       zE("messenger:on", "close", function () {
    //         doMessengerHide();
    //         [50, 200, 500, 1000, 2000].forEach(function (ms) { setTimeout(doMessengerHide, ms); });
    //       });
    //     } catch (e) {}
    //   };
    //   document.body.appendChild(script);
    // }
    // Firebase 动态加载（GA 埋点 + Performance 上报），pro / qa 上报
    import("@/utils/firebase").then((m) => m.shouldInitFirebase() && m.initFirebase()).catch(() => {});
  }, []);

  // 语言切换时同步更新 Zendesk widget 语言（已注释，后续可能恢复）
  // React.useEffect(() => {
  //   if (typeof window === "undefined") return;
  //   const zE = window.zE || window.$zE;
  //   if (typeof zE !== "function") return;
  //   const zenLocale = getZendeskLocale(XtLocale.getZenLan());
  //   try {
  //     zE("webWidget", "setLocale", zenLocale);
  //     zE("messenger:set", "locale", zenLocale);
  //   } catch (e) {}
  // }, [locale]);

  // Facebook Pixel：路由变化时发送 PageView（带 content_name 便于多项目区分）
  React.useEffect(() => {
    if (typeof window === "undefined" || !getFbPixelId()) return;
    trackFbPixelPageView({ content_name: "user-center" });
  }, [router.pathname]);

  return (
    <XtProvider payload={payload} intlValue={XtLocale.intl}>
      <ConfigProvider locale={localeObj.antd} direction={isRtl ? "rtl" : "ltr"} autoInsertSpaceInButton={false}>
        <Seo tdk={pageProps.tdk || {}} resolvedUrl={seoResolvedUrl} locale={seoLocale} />
        <Head>
          <meta name="version" content={`${Package.version}`} key="version" />
          <meta key="viewport" name="viewport" content="minimal-ui,width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover" />
          <link rel="manifest" href={`/manifest.json?v=${APP_ICON_VERSION}`} />
          <link rel="icon" href={faviconIco} sizes="any" />
          <meta name="theme-color" content="#181a20" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="default" />
          <meta name="apple-mobile-web-app-title" content="Spikex" />
          <link rel="apple-touch-icon" href={`/icons/icon-180x180.png?v=${APP_ICON_VERSION}`} sizes="180x180" />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="msapplication-TileColor" content="#B9F78A" />
          <meta name="msapplication-TileImage" content={`/icons/icon-192x192.png?v=${APP_ICON_VERSION}`} />
        </Head>
        {/* Firebase Performance：First Input Delay polyfill，pro / qa 加载 */}
        {["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV) && (
          <Script
            src="https://cdn.jsdelivr.net/npm/first-input-delay@0.1.3/dist/first-input-delay.min.js"
            strategy="beforeInteractive"
          />
        )}
        {/* Meta Facebook Pixel：与 ssr-web-index 同 ID 时用 content_name 区分项目，见 .env 与 utils/facebookPixel */}
        {getFbPixelId() && (
          <>
            <Script
              id="facebook-pixel"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${getFbPixelId()}');
fbq('track', 'PageView', { content_name: 'user-center' }${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE ? `, { test_event_code: '${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE}' }` : ""});
                `,
              }}
            />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: "none" }}
                src={`https://www.facebook.com/tr?id=${getFbPixelId()}&ev=PageView&noscript=1`}
                alt=""
              />
            </noscript>
          </>
        )}
        <Script
          id="app-before-interactive"
          data-theme="dark"
          strategy="beforeInteractive"
          src={domain + "/beforeInteractive.js?v=" + Package.version}
        />
        <Script src={domain + "/iconpark.js?v=" + Package.version} />
        <link rel="stylesheet" type="text/css" href="https://at.alicdn.com/t/font_2502537_ytndssfkiy.css" />
        {/* Start of xtsupport Zendesk Widget script (Replaced by LiveHelp100) */}
        {/* <Script
          id={"ze-snippet"}
          src={domain + "/snippet.js?key=6509f2a2-20a9-4ed5-95f9-1ca80cd1d807"}
          onLoad={() => {
            const zE = window.zE || window.$zE;
            if (typeof zE === "function") {
              try {
                zE("webWidget", "setLocale", XtLocale.getZenLan());
              } catch (e) {}
            }
          }}
        /> */}
        {/* End of xtsupport Zendesk Widget script */}
        {/*      <Script*/}
        {/*        id={"sensorsScript"}*/}
        {/*        strategy="afterInteractive"*/}
        {/*        dangerouslySetInnerHTML={{*/}
        {/*          __html: `*/}
        {/*    var sensors = window['sensorsDataAnalytic201505'];*/}
        {/*    sensors.init({*/}
        {/*      server_url: '${process.env.NEXT_PUBLIC_sensorsSeverUrl}',*/}
        {/*      is_track_single_page: true,*/}
        {/*      use_client_time:false,*/}
        {/*      send_type:'beacon',*/}
        {/*      heatmap: {*/}
        {/*        clickmap:'default',*/}
        {/*        scroll_notice_map:'default'*/}
        {/*      }*/}
        {/*    });*/}
        {/*`,*/}
        {/*        }}*/}
        {/*      />*/}
        <ContextToStore>
          <Component {...pageProps} />
          <PWAInstallPrompt />
          <PWAUpdatePrompt />
          {/* <CustomerService /> */}
        </ContextToStore>
      </ConfigProvider>
    </XtProvider>
  );
}

export default MyApp;

export function reportWebVitals(metric) {
  const { pathname, route, locale } = reportWebVitals.nextRouterInfo || {};
}

/*
function generateNonceStr(length, type) {
  let chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  type === "num" && (chars = "0123456789");
  const maxPos = chars.length;
  let noceStr = "";
  for (let i = 0; i < (length || 32); i++) {
    noceStr += chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return noceStr;
}

MyApp.getInitialProps = (context) => {
  let COOKIES = {};
  try {
    COOKIES = context.ctx.req.cookies;
  } catch (e) {}

  return {
    COOKIES,
  };
};
 */
